import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { CellProps, Column } from '@lib/components/Table/types';
import { ChipTypes } from '@lib/components/Chip/Chip';
import ChipCell from '@lib/components/Table/commonComponents/ChipCell/ChipCell';
import { PerformanceType } from '../types';
import { StatusKind, MonitoringEntity } from '../enums';

type Args = {
  statusKind?: StatusKind;
  monitoringEntity: MonitoringEntity;
  t: TFunction<'translation', undefined>;
};

const getPerformanceTableColumns = ({
  statusKind,
  monitoringEntity,
  t,
}: Args) => {
  const columns: Column<PerformanceType>[] = [];
  if (monitoringEntity === MonitoringEntity.Site) {
    columns.push({
      id: 'name',
      Header: t('site'),
      accessor: 'site',
      Cell: (props: CellProps<PerformanceType>) => {
        const label = _get(props, 'item.name', '');
        if (!label) return '-';
        return <ChipCell label={label} type={ChipTypes.chipGray} />;
      },
      sortable: true,
      maxWidth: 250,
    });
  } else if (monitoringEntity === MonitoringEntity.Sector) {
    columns.push({
      id: 'name',
      Header: t('sector'),
      accessor: 'sector',
      Cell: (props: CellProps<PerformanceType>) => {
        const label = _get(props, 'item.name', '');
        if (!label) return '-';
        return <ChipCell label={label} type={ChipTypes.chipGray} />;
      },
      sortable: true,
      maxWidth: 250,
    });
  }

  if (statusKind === StatusKind.WarningAndAlert) {
    columns.push({
      id: 'totalAnomaliesByTaskResults',
      Header: t('warning-and-alert'),
      accessorFn: ({ item }: CellProps<PerformanceType>) => {
        // Normalize according to anomaly kind and entity
        const value = _get(item, 'totalAnomaliesByTaskResults');
        if (value === null || value === undefined) return '-';
        return value;
      },
      sortable: true,
      width: 120,
    });
  }
  if (statusKind === StatusKind.Warning) {
    columns.push({
      id: 'totalAnomaliesByTaskResults',
      Header: t('warning'),
      accessorFn: ({ item }: CellProps<PerformanceType>) => {
        // Normalize according to anomaly kind and entity
        const value = _get(item, 'totalAnomaliesByTaskResults');
        if (value === null || value === undefined) return '-';
        return value;
      },
      sortable: true,
      width: 120,
    });
  }
  if (statusKind === StatusKind.Alert) {
    columns.push({
      id: 'totalAnomaliesByTaskResults',
      Header: t('alert'),
      accessorFn: ({ item }: CellProps<PerformanceType>) => {
        // Normalize according to anomaly kind and entity
        const value = _get(item, 'totalAnomaliesByTaskResults');
        if (value === null || value === undefined) return '-';
        return value;
      },
      sortable: true,
      width: 120,
    });
  }
  if (
    statusKind === StatusKind.WarningAndAlert ||
    statusKind === StatusKind.Warning ||
    statusKind === StatusKind.Alert
  ) {
    columns.push({
      id: 'successPercentageByTaskResults',
      Header: t('performance-label'),
      accessorFn: ({ item }: CellProps<PerformanceType>) => {
        const val = _get(item, 'successPercentageByTaskResults');
        if (val === null || val === undefined) return '-';
        if (!val) return '0';
        if (val % 1 === 0) return String(val);
        return Number(val).toFixed(2);
      },
      sortable: true,
      width: 150,
    });
  }
  if (statusKind === StatusKind.DeadlineMissed) {
    columns.push(
      {
        id: 'totalAnomaliesByTaskDeadlinesMissed',
        Header: t('deadline-missed'),
        accessorFn: ({ item }: CellProps<PerformanceType>) => {
          // Normalize according to anomaly kind and entity
          const value = _get(item, 'totalAnomaliesByTaskDeadlinesMissed');
          if (value === null || value === undefined) return '-';
          return value;
        },
        headerTooltipInfo: {
          tooltipBody: t('total-anomalies-by-task-deadlines-missed-desc'),
          icon: 'info',
          symbolsOutlined: true,
        },
        sortable: true,
        width: 120,
      },
      {
        id: 'successPercentageByTaskDeadlinesMissed',
        Header: t('performance-label'),
        accessorFn: ({ item }: CellProps<PerformanceType>) => {
          const val = _get(item, 'successPercentageByTaskDeadlinesMissed');
          if (val === null || val === undefined) return '-';
          if (!val) return '0';
          if (val % 1 === 0) return String(val);
          return Number(val).toFixed(2);
        },
        sortable: true,
        width: 150,
      },
    );
  }
  return columns;
};

export default getPerformanceTableColumns;
